import type { Rarity, Reward } from '@anidex/shared/economy';
import { boosterShortName, rarityName, roleName, unlockLabel } from '../i18n/catalog';
import i18n, { intlLocale } from '../i18n';

function locale() {
  return intlLocale();
}

export const formatNumber = (value: number) => new Intl.NumberFormat(locale()).format(value);

export const formatCompact = (value: number) =>
  value < 10_000
    ? new Intl.NumberFormat(locale()).format(value)
    : new Intl.NumberFormat(locale(), { notation: 'compact', maximumFractionDigits: 1 }).format(value);

export const formatDate = (iso: string) =>
  new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

export const formatMonth = (iso: Date) => new Intl.DateTimeFormat(locale(), { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(iso);

export function formatMonthKey(key: string): string {
  const [year, month] = key.split('-').map(Number);
  if (!year || !month) return key;
  return formatMonth(new Date(Date.UTC(year, month - 1, 1)));
}


export const formatPercent = (value: number) =>
  `${value.toLocaleString(locale(), { minimumFractionDigits: 3, maximumFractionDigits: 3 })} %`;

export const rarityLabel = (rarity: Rarity) => rarityName(rarity);

export const roleLabel = (role: string | null) => (role ? roleName(role) : null);

export function rewardParts(reward: Reward): string[] {
  const parts: string[] = [];
  if (reward.anima) parts.push(`+${formatNumber(reward.anima)} Anima`);
  if (reward.xp) parts.push(`+${formatNumber(reward.xp)} EXP`);
  if (reward.fragments) parts.push(i18n.t('rewards.fragments', { count: reward.fragments, formatted: formatNumber(reward.fragments) }));
  const packs = [
    ['standard', reward.boosters?.standard ?? 0],
    ['premium', reward.boosters?.premium ?? 0],
    ['legendary', reward.boosters?.legendary ?? 0],
  ] as const;
  for (const [id, count] of packs) {
    if (count > 0) {
      parts.push(i18n.t('rewards.booster', { count, name: boosterShortName(id, id) }));
    }
  }
  if (reward.characterRarity) parts.push(i18n.t('rewards.character', { rarity: rarityLabel(reward.characterRarity) }));
  for (const unlock of reward.unlocks ?? []) parts.push(unlockLabel(unlock.kind, unlock.key, unlock.label));
  return parts;
}

export function timeUntil(iso: string, now = Date.now()): string {
  const totalSeconds = Math.max(0, Math.ceil((Date.parse(iso) - now) / 1000));
  const days = Math.floor(totalSeconds / 86400);
  if (days > 0) {
    const dayHours = Math.floor((totalSeconds % 86400) / 3600);
    return dayHours > 0 ? i18n.t('time.daysHours', { days, hours: dayHours }) : i18n.t('time.days', { count: days });
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return i18n.t('time.hours', { hours, minutes: String(minutes).padStart(2, '0') });
  if (minutes > 0) return i18n.t('time.minutes', { minutes, seconds: String(seconds).padStart(2, '0') });
  return i18n.t('time.seconds', { count: seconds });
}
